import ModernApiController from "@/api/ModernApiController";
import { IsProblemResult } from "@/helpers/newUserSelector";
import { Team, TeamFilter } from "@/teams";
import { PagedResult } from "@infoland/iprova-api";

export default {
    /**
     * Gets a list of teams
     * @param filter (optional) A filter to use to find only specific teams
     * @param includeMemberships (optional) whether to include the team memberships (default: true)
     * @param offset (optional, default 0) The offset from which to start returning teams from the list
     * @param maxResults (optional, default 100) The maximum teans to return at one time
     * @param suppressErrors Whether to supress the throwing of exceptions on error
     * @returns a paged result of teams
     */
    async getTeamsAsync(
        filter: TeamFilter = undefined,
        includeMemberships: boolean = true,
        offset: number = 0,
        maxResults: number = 100,
        suppressErrors: boolean = false
    ): Promise<PagedResult<Team> | null> {
        let path: string;

        if (filter && filterRequiresPost(filter)) {
            const filterResponse: any = await ModernApiController.postAsync<TeamFilter, any>("/teams/filter", filter, suppressErrors);

            if (!IsProblemResult(filterResponse)) {
                const createdIdentifier: string = filterResponse.created_identifier;
                path = `/teams/filter/${createdIdentifier}?include_memberships=${includeMemberships}&limit=${maxResults}&offset=${offset}`;
            } else return null;
        } else {
            path = `teams?include_memberships=${includeMemberships}&limit=${maxResults}&offset=${offset}`;
        }
        const teamResponse: PagedResult<Team> = await ModernApiController.getPagedResultAsync<Team>(path, suppressErrors);

        if (!IsProblemResult(teamResponse)) return teamResponse;
        else return null;
    }
};

/**
 * Checks whether the given filter requires that a 'post' filter be used (because of url length limitations)
 * @param filter The filter to check
 * @returns true if the filter requires a POST request to be done first, otherwise false
 */
function filterRequiresPost(filter: TeamFilter): boolean {
    // check if any arrays are empty. If so, they should not go over the url
    if (filter) {
        const keys: (keyof TeamFilter)[] = Object.keys(filter) as (keyof TeamFilter)[];
        for (let k: number = 0; k < keys.length; k++) {
            const prop = filter[keys[k]];
            if (Array.isArray(prop) && prop.length == 0) return true;
        }
    }

    // require a post for every filter with team ids
    if (filter.team_ids) return true;

    // require a post if there is a name filter
    if (filter.name) return true;

    // all other cases do not require a post filter
    return false;
}
